import React, { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Link } from 'react-router-dom';
import { X, Lock, CreditCard, CheckCircle, MapPin, Truck } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { createPaymentIntent, recordOrder, type ShippingAddress } from '../api/hpanel';
import type { CartItem } from '../context/CartContext';
import {
  SHIPPING_LOCAL_GBP,
  SHIPPING_INTERNATIONAL_GBP,
  getShippingCost,
  isLocalByCountry,
  COUNTRY_OPTIONS,
  type ShippingOption,
} from '../config/shipping';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? "");

interface CheckoutFormProps {
  totalWithShipping: number;
  items: CartItem[];
  onSuccess: (orderId?: string) => void;
  shippingOption: ShippingOption;
  shippingCost: number;
  shippingAddress: ShippingAddress | null;
  customerEmail?: string;
}

const CheckoutForm = ({ totalWithShipping, items, onSuccess, shippingOption, shippingCost, shippingAddress, customerEmail }: CheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setError(null);
    setProcessing(true);

    try {
      const { clientSecret, paymentIntentId } = await createPaymentIntent({
        amount: totalWithShipping,
        currency: 'GBP',
        lineItems: items.map((i) => ({ id: i.id, name: i.name, price: i.price, quantity: i.quantity })),
      });
      if (!clientSecret) throw new Error('No client secret returned');

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement)! },
      });
      if (confirmError) {
        setError(confirmError.message || 'Payment failed');
        setProcessing(false);
        return;
      }
      if (paymentIntent?.status === 'succeeded') {
        const result = await recordOrder({
          paymentMethod: 'stripe',
          paymentId: paymentIntent.id || paymentIntentId || '',
          amount: totalWithShipping,
          currency: 'GBP',
          lineItems: items,
          shippingOption,
          shippingCost,
          shippingAddress: shippingAddress || undefined,
          customerEmail,
        });
        onSuccess(result.orderId);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border border-gray-200 rounded-xl bg-white">
        <CardElement options={{
            style: {
                base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': { color: '#aab7c4' },
                },
            },
        }}/>
      </div>
      {error && <div className="text-red-500 text-sm">{error}</div>}
      <button 
        type="submit" 
        disabled={!stripe || processing}
        className="w-full bg-simba-dark text-white py-4 rounded-xl font-bold hover:bg-simba-green transition-colors disabled:opacity-50"
      >
        {processing ? 'Processing...' : `Pay £${totalWithShipping.toFixed(2)}`}
      </button>
    </form>
  );
};

const defaultAddress: ShippingAddress = {
  line1: '',
  line2: '',
  city: '',
  postcode: '',
  country: 'GB',
  countryName: 'United Kingdom',
};

export const CartDrawer = () => {
  const { items, isOpen, toggleCart, removeFromCart, updateQuantity, clearCart, total } = useCart();
  const [checkoutMode, setCheckoutMode] = useState<'cart' | 'checkout'>('cart');
  const [paymentMethod, setPaymentMethod] = useState<'stripe' | 'paypal'>('stripe');
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>(defaultAddress);
  const [shippingOption, setShippingOption] = useState<ShippingOption>('local');
  const [customerEmail, setCustomerEmail] = useState('');
  const [lastOrderId, setLastOrderId] = useState<string | null>(null);

  const shippingCost = getShippingCost(shippingOption);
  const totalWithShipping = total + shippingCost;

  useEffect(() => {
    const isLocal = isLocalByCountry(shippingAddress.country);
    setShippingOption(isLocal ? 'local' : 'international');
  }, [shippingAddress.country]);

  const handleCountryChange = (code: string) => {
    const option = COUNTRY_OPTIONS.find((c) => c.code === code);
    setShippingAddress((prev) => ({
      ...prev,
      country: code,
      countryName: option?.name ?? prev.countryName,
    }));
    if (code === 'GB' || code === 'UK') setShippingOption('local');
    else if (code && code !== 'OTHER') setShippingOption('international');
    else if (code === 'OTHER') setShippingOption('international');
  };

  const handlePaymentSuccess = (orderId?: string) => {
    setLastOrderId(orderId || null);
    setPaymentSuccess(true);
    clearCart();
    setTimeout(() => {
      setPaymentSuccess(false);
      setCheckoutMode('cart');
      setLastOrderId(null);
      toggleCart();
    }, 8000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={toggleCart}></div>

      {/* Drawer */}
      <div className="absolute inset-y-0 right-0 max-w-md w-full bg-simba-cream shadow-2xl flex flex-col transform transition-transform duration-300">
        
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white">
          <h2 className="font-display font-bold text-xl text-simba-dark">
            {checkoutMode === 'cart' ? 'Your Bag' : 'Secure Checkout'}
          </h2>
          <button onClick={toggleCart} className="text-gray-400 hover:text-simba-dark">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
          {paymentSuccess ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-8 px-4">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-simba-green">
                <CheckCircle size={48} />
              </div>
              <h3 className="font-display text-xl font-bold text-simba-dark">Thank you</h3>
              <p className="text-gray-600">Your order has been received. We&apos;ll be in touch soon.</p>
              {lastOrderId && (
                <div className="bg-white p-4 rounded-xl border border-gray-100 w-full text-left space-y-2">
                  <p className="text-sm font-bold text-simba-dark">Order reference: <span className="font-mono">{lastOrderId}</span></p>
                  <p className="text-xs text-gray-500">Save this to track your shipment. We&apos;ll email you when it&apos;s on its way.</p>
                  <Link to="/track-order" className="inline-block text-sm font-bold text-simba-green hover:underline">Track your order →</Link>
                </div>
              )}
            </div>
          ) : items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4 text-gray-400">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                    <Lock size={24} />
                </div>
                <p>Your bag is empty.</p>
                <button onClick={toggleCart} className="text-simba-green font-bold">Start Shopping</button>
            </div>
          ) : (
            <>
              {checkoutMode === 'cart' ? (
                 <div className="space-y-6">
                    {items.map(item => (
                        <div key={item.id} className="flex gap-4">
                            <div className="w-20 h-20 bg-white rounded-lg p-2 border border-gray-100 flex-shrink-0">
                                <img src={item.image} alt={item.name} className="w-full h-full object-contain mix-blend-multiply" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-bold text-simba-dark text-sm leading-tight mb-1">{item.name}</h3>
                                <p className="text-sm text-gray-500 mb-2">£{item.price.toFixed(2)}</p>
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center border border-gray-200 rounded-lg bg-white">
                                        <button onClick={() => updateQuantity(item.id, -1)} className="px-2 py-1 text-gray-500 hover:text-simba-dark">-</button>
                                        <span className="text-xs font-bold px-1">{item.quantity}</span>
                                        <button onClick={() => updateQuantity(item.id, 1)} className="px-2 py-1 text-gray-500 hover:text-simba-dark">+</button>
                                    </div>
                                    <button onClick={() => removeFromCart(item.id)} className="text-xs text-red-400 hover:text-red-500 underline">Remove</button>
                                </div>
                            </div>
                        </div>
                    ))}
                 </div>
              ) : (
                 <div className="space-y-6">
                    {/* Trust Signals */}
                    <div className="bg-green-50 text-simba-green px-4 py-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 border border-simba-green/20">
                       <Lock size={14} /> 
                       <span>SSL Secured & Encrypted Payment</span>
                    </div>

                    {/* Contact & Shipping */}
                    <div className="bg-white p-4 rounded-xl border border-gray-100">
                      <h3 className="font-display font-bold text-simba-dark text-sm mb-3 flex items-center gap-2">
                        <MapPin size={16} /> Contact & shipping address
                      </h3>
                      <div className="space-y-3">
                        <input
                          type="email"
                          placeholder="Your email *"
                          value={customerEmail}
                          onChange={(e) => setCustomerEmail(e.target.value)}
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-simba-green"
                        />
                        <input
                          type="text"
                          placeholder="Address line 1 *"
                          value={shippingAddress.line1}
                          onChange={(e) => setShippingAddress((a) => ({ ...a, line1: e.target.value }))}
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-simba-green"
                        />
                        <input
                          type="text"
                          placeholder="Address line 2 (optional)"
                          value={shippingAddress.line2 || ''}
                          onChange={(e) => setShippingAddress((a) => ({ ...a, line2: e.target.value }))}
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-simba-green"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            placeholder="City *"
                            value={shippingAddress.city}
                            onChange={(e) => setShippingAddress((a) => ({ ...a, city: e.target.value }))}
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-simba-green"
                          />
                          <input
                            type="text"
                            placeholder="Postcode *"
                            value={shippingAddress.postcode}
                            onChange={(e) => setShippingAddress((a) => ({ ...a, postcode: e.target.value }))}
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-simba-green"
                          />
                        </div>
                        <select
                          value={shippingAddress.country || 'GB'}
                          onChange={(e) => handleCountryChange(e.target.value)}
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-simba-green bg-white"
                        >
                          {COUNTRY_OPTIONS.map((c) => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Shipping Method */}
                    <div className="bg-white p-4 rounded-xl border border-gray-100">
                      <h3 className="font-display font-bold text-simba-dark text-sm mb-3 flex items-center gap-2">
                        <Truck size={16} /> Shipping method
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setShippingOption('local')}
                          className={`p-4 rounded-xl border text-left transition-all ${
                            shippingOption === 'local'
                              ? 'border-simba-green bg-simba-green/5 text-simba-dark font-bold'
                              : 'border-gray-200 text-gray-500 hover:border-gray-300'
                          }`}
                        >
                          <span className="block text-sm">UK (Local)</span>
                          <span className="block text-lg font-display">£{SHIPPING_LOCAL_GBP.toFixed(2)}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setShippingOption('international')}
                          className={`p-4 rounded-xl border text-left transition-all ${
                            shippingOption === 'international'
                              ? 'border-simba-green bg-simba-green/5 text-simba-dark font-bold'
                              : 'border-gray-200 text-gray-500 hover:border-gray-300'
                          }`}
                        >
                          <span className="block text-sm">International</span>
                          <span className="block text-lg font-display">£{SHIPPING_INTERNATIONAL_GBP.toFixed(2)}</span>
                        </button>
                      </div>
                    </div>

                    {/* Order Summary Receipt Style */}
                    <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm relative overflow-hidden">
                        {/* Perforated Top Decoration */}
                        <div className="absolute top-0 left-0 right-0 h-4 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIxMCIgdmlld0JveD0iMCAwIDIwIDEwIj48Y2lyY2xlIGN4PSIxMCIgY3k9Ii01IiByPSI4IiBmaWxsPSIjRjNGNEY2Ii8+PC9zdmc+')] bg-repeat-x opacity-50"></div>
                        
                        <h3 className="font-display font-bold text-center text-lg mb-6 text-simba-dark uppercase tracking-widest border-b-2 border-dashed border-gray-100 pb-4">Order Summary</h3>

                        <div className="space-y-4 mb-6">
                           {items.map(item => (
                              <div key={item.id} className="flex gap-4 items-center bg-gray-50 p-3 rounded-lg border border-gray-100">
                                 {/* Image */}
                                 <div className="w-16 h-16 bg-white rounded-md p-1 border border-gray-200 flex-shrink-0">
                                    <img src={item.image} alt={item.name} className="w-full h-full object-contain mix-blend-multiply" />
                                 </div>
                                 
                                 {/* Details & Controls */}
                                 <div className="flex-1 min-w-0">
                                    <h4 className="font-bold text-simba-dark text-xs leading-tight mb-1 truncate">{item.name}</h4>
                                    <div className="flex items-center justify-between">
                                       <span className="text-sm font-medium text-simba-gold">£{(item.price * item.quantity).toFixed(2)}</span>
                                       
                                       {/* Mini Controls */}
                                       <div className="flex items-center bg-white border border-gray-200 rounded-md h-7">
                                          <button onClick={() => updateQuantity(item.id, -1)} className="px-2 h-full text-gray-500 hover:text-simba-dark flex items-center">-</button>
                                          <span className="text-xs font-bold px-1 min-w-[1.5rem] text-center">{item.quantity}</span>
                                          <button onClick={() => updateQuantity(item.id, 1)} className="px-2 h-full text-gray-500 hover:text-simba-dark flex items-center">+</button>
                                       </div>
                                    </div>
                                 </div>
                              </div>
                           ))}
                        </div>

                        <div className="border-t-2 border-dashed border-gray-100 pt-4 space-y-2">
                           <div className="flex justify-between text-sm text-gray-500">
                              <span>Subtotal</span>
                              <span>£{total.toFixed(2)}</span>
                           </div>
                           <div className="flex justify-between text-sm text-gray-500">
                              <span>Shipping ({shippingOption === 'local' ? 'UK' : 'International'})</span>
                              <span className="text-simba-green font-bold">£{shippingCost.toFixed(2)}</span>
                           </div>
                           <div className="flex justify-between text-lg font-bold text-simba-dark pt-2 mt-2 border-t border-gray-100">
                              <span>Total</span>
                              <span>£{totalWithShipping.toFixed(2)}</span>
                           </div>
                        </div>
                    </div>

                    {/* Payment Method Toggle */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-3 ml-1">Select Payment Method</label>
                        <div className="grid grid-cols-2 gap-3">
                           <button 
                               onClick={() => setPaymentMethod('stripe')}
                               className={`p-4 rounded-xl border flex flex-col items-center justify-center space-y-2 transition-all ${
                                   paymentMethod === 'stripe' 
                                     ? 'border-simba-green bg-simba-green/5 text-simba-green shadow-inner' 
                                     : 'border-gray-200 text-gray-400 hover:border-gray-300'
                               }`}
                           >
                               <CreditCard size={24} />
                               <span className="text-xs font-bold">Card Payment</span>
                           </button>
                           <button 
                               onClick={() => setPaymentMethod('paypal')}
                               className={`p-4 rounded-xl border flex flex-col items-center justify-center space-y-2 transition-all ${
                                   paymentMethod === 'paypal' 
                                     ? 'border-[#0070BA] bg-[#0070BA]/5 text-[#0070BA] shadow-inner' 
                                     : 'border-gray-200 text-gray-400 hover:border-gray-300'
                               }`}
                           >
                               <span className="italic font-bold font-serif text-xl">PayPal</span>
                           </button>
                        </div>
                    </div>

                    {/* Payment Implementation */}
                    {paymentMethod === 'stripe' && (
                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                           <Elements stripe={stripePromise}>
                               <CheckoutForm
                                 totalWithShipping={totalWithShipping}
                                 items={items}
                                 onSuccess={handlePaymentSuccess}
                                 shippingOption={shippingOption}
                                 shippingCost={shippingCost}
                                 shippingAddress={shippingAddress.line1 ? shippingAddress : null}
                                 customerEmail={customerEmail || undefined}
                               />
                           </Elements>
                        </div>
                    )}

                    {paymentMethod === 'paypal' && (
                        <div className="mt-4">
                           {process.env.REACT_APP_PAYPAL_CLIENT_ID ? (
                             <PayPalScriptProvider options={{ clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID, currency: "GBP" }}>
                               <PayPalButtons 
                                   style={{ layout: "vertical", shape: "rect", borderRadius: 12 }} 
                                   createOrder={(_data, actions) => {
                                       return actions.order.create({
                                           intent: "CAPTURE",
                                           purchase_units: [
                                               {
                                                   amount: {
                                                       value: totalWithShipping.toFixed(2),
                                                       currency_code: "GBP"
                                                   },
                                               },
                                           ],
                                       });
                                   }}
                                   onApprove={async (data, actions) => {
                                       if (!actions.order) return;
                                       await actions.order.capture();
                                       try {
                                         const result = await recordOrder({
                                           paymentMethod: 'paypal',
                                           paymentId: data.orderID,
                                           amount: totalWithShipping,
                                           currency: 'GBP',
                                           lineItems: items,
                                           shippingOption,
                                           shippingCost,
                                           shippingAddress: shippingAddress.line1 ? shippingAddress : undefined,
                                           customerEmail: customerEmail || undefined,
                                         });
                                         handlePaymentSuccess(result.orderId);
                                       } catch (e) {
                                         console.error('Record order failed:', e);
                                       }
                                   }}
                                   onError={(err) => console.error('PayPal error:', err)}
                               />
                             </PayPalScriptProvider>
                           ) : (
                             <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                               <p className="font-bold mb-1">PayPal not configured</p>
                               <p>Add <code className="bg-amber-100 px-1 rounded">REACT_APP_PAYPAL_CLIENT_ID</code> to your <code className="bg-amber-100 px-1 rounded">.env</code> (see PAYPAL-SETUP.md).</p>
                             </div>
                           )}
                        </div>
                    )}
                 </div>
              )}
            </>
          )}
        </div>

        {/* Footer Actions */}
        {items.length > 0 && !paymentSuccess && (
            <div className="p-6 bg-white border-t border-gray-100">
                {checkoutMode === 'cart' ? (
                    <>
                        <div className="flex justify-between text-lg font-bold text-simba-dark mb-4">
                            <span>Total</span>
                            <span>£{total.toFixed(2)}</span>
                        </div>
                        <button 
                            onClick={() => setCheckoutMode('checkout')}
                            className="w-full bg-simba-green text-white py-4 rounded-xl font-bold hover:bg-simba-dark transition-colors shadow-lg flex justify-center items-center"
                        >
                            Proceed to Checkout <Lock size={16} className="ml-2 opacity-70" />
                        </button>
                    </>
                ) : (
                    <button 
                        onClick={() => setCheckoutMode('cart')}
                        className="w-full text-gray-500 font-bold hover:text-simba-dark"
                    >
                        Back to Cart
                    </button>
                )}
            </div>
        )}

      </div>
    </div>
  );
};
